import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { KeyboardArrowDown, Person, Inventory } from "@mui/icons-material";

const ExpandableOrdersTable = ({ orders, type }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (jobNo) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(jobNo)) {
      newExpandedRows.delete(jobNo);
    } else {
      newExpandedRows.add(jobNo);
    }
    setExpandedRows(newExpandedRows);
  };

  const isExpanded = (jobNo) => expandedRows.has(jobNo);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
            <TableCell width="50px" />
            <TableCell>
              <Typography variant="subtitle2" fontWeight="bold">
                Job No
              </Typography>
            </TableCell>
            {type === "picking" && (
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  Picker
                </Typography>
              </TableCell>
            )}
            <TableCell align="center">
              <Typography variant="subtitle2" fontWeight="bold">
                Items Count
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <React.Fragment key={order.job_no}>
              {/* Main Row */}
              <TableRow
                hover
                onClick={() => toggleRow(order.job_no)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent row's onClick from firing
                      toggleRow(order.job_no);
                    }}
                    sx={{
                      transition: "transform 0.2s",
                      transform: isExpanded(order.job_no)
                        ? "rotate(0deg)"
                        : "rotate(-90deg)",
                    }}
                  >
                    <KeyboardArrowDown />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {order?.job_no}
                  </Typography>
                </TableCell>
                {type === "picking" && (
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="body2">
                        {order.picker || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                )}
                <TableCell align="center">
                  <Chip
                    label={order.items?.length || 0}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>

              {/* Expanded Row */}
              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={type === "picking" ? 4 : 3}
                >
                  <Collapse
                    in={isExpanded(order.job_no)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box sx={{ margin: 1 }}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        component="div"
                        sx={{
                          color: "#1976d2",
                          fontWeight: "bold",
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Inventory fontSize="small" />
                        Line Item Details
                      </Typography>
                      <Table size="small" aria-label="item details">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f0f8ff" }}>
                            <TableCell>
                              <Typography variant="caption" fontWeight="bold">
                                Stock Code
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" fontWeight="bold">
                                Description
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="caption" fontWeight="bold">
                                Order Qty
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {order.items?.map((item) => (
                            <TableRow key={item.line_id}>
                              <TableCell>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "18px",
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    padding: "2px 6px",
                                    borderRadius: 1,
                                  }}
                                >
                                  {item.stock_code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    padding: "2px 6px",
                                  }}
                                >
                                  {item.stock_description}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={item.order_qty}
                                  size="small"
                                  color="secondary"
                                  sx={{ minWidth: 40 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExpandableOrdersTable;
